import React, { useState, useEffect, useCallback } from "react";
import { Radar, Fingerprint, LogIn, Bell, MapPinPlus } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { notifications as notifApi } from "../services/api";
import { useTranslation } from "react-i18next";


function Navigation() {
  const location = useLocation();
  const { isAuthenticated, token } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const { t } = useTranslation();

  const fetchUnread = useCallback(async () => {
    if (!isAuthenticated || !token) return;
    try {
      const { count } = await notifApi.getUnreadCount(token);
      setUnreadCount(count);
    } catch {
      // ignore
    }
  }, [isAuthenticated, token]);

  useEffect(() => {
    fetchUnread();
    const interval = setInterval(() => {
      if (!document.hidden) fetchUnread();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchUnread]);

  // Reset count when visiting notifications page
  useEffect(() => {
    if (location.pathname === "/notifications") {
      setUnreadCount(0);
    }
  }, [location.pathname]);

  const navItems = [
    { to: "/", icon: Radar, label: t("nav.explore") },
    { to: "/upload", icon: MapPinPlus, label: t("nav.pin") },
    ...(isAuthenticated
      ? [{ to: "/notifications", icon: Bell, label: t("nav.alerts"), badge: unreadCount }]
      : []),
    isAuthenticated
      ? { to: "/profile", icon: Fingerprint, label: t("nav.you") }
      : { to: "/login", icon: LogIn, label: t("nav.login") },
  ];

  return (
    <div className="bg-th-card border-t border-th-border">
      <div className="flex justify-around items-center py-2 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        {navItems.map((item) => {
          const isActive = location.pathname === item.to;
          const Icon = item.icon;
          return (
            <Link
              key={item.to}
              to={item.to}
              className={`relative flex flex-col items-center py-1 px-4 transition-colors ${
                isActive
                  ? "text-th-accent"
                  : "text-th-muted active:text-th-text-sec"
              }`}
            >
              <div className="relative h-6 flex items-center justify-center">
                <Icon size={item.size || 24} strokeWidth={isActive ? 2.5 : 1.5} />
                {item.badge > 0 && (
                  <span className="absolute -top-1.5 -right-2 min-w-[16px] h-4 bg-red-500 text-white text-[11px] font-bold rounded-full flex items-center justify-center px-1">
                    {item.badge > 9 ? "9+" : item.badge}
                  </span>
                )}
              </div>
              <span className="text-[11px] font-medium mt-1 tracking-wide uppercase font-mono">
                {item.label}
              </span>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

export default Navigation;
